import React, { useEffect, useState } from 'react';
import { Box, useTheme } from '@mui/material';
import parse from 'html-react-parser';
import sanitizeHtml from 'sanitize-html';

import TweetEmbed from '@/DesignSystem/features/TweetEmbed';
import CustomImageNoticiasComponent from '@/components/shared/custom/custom-image-noticias';
import NoticiaFuente from './NoticiaFuente';
import LiteYoutubePreview from '@/DesignSystem/features/YoutubePreview';
import YoutubePreviewContent from '@/DesignSystem/features/YoutubePreviewContent';

const sanitizeOptions = {
    allowedTags: sanitizeHtml.defaults.allowedTags.concat(['img', 'iframe']),
    allowedAttributes: {
        '*': ['class', 'style', 'id', 'href'],
        img: ['src', 'alt', 'title', 'width', 'height'],
        iframe: ['src', 'width', 'height', 'frameborder', 'allow', 'allowfullscreen'],
    },
    allowedSchemes: ['http', 'https', 'data'],
    allowedSchemesByTag: {
        img: ['http', 'https', 'data'],
    },
    selfClosing: ['img', 'br', 'hr'],
};

const TweetWidget = ({ tweetId, index }) => {
    const theme = useTheme();
    return (
        <Box key={`tweet-${index}`} className="tweet-container" sx={{ margin: '16px 0' }}>
            <TweetEmbed
                tweetId={tweetId}
                options={{
                    theme: theme.palette.mode,
                    align: 'center',
                }}
            />
        </Box>
    );
};

const TwitchWidget = ({ content, isVideo, index }) => {
    const [hostname, setHostname] = useState('localhost');

    useEffect(() => {
        setHostname(window.location.hostname);
    }, []);

    const src = isVideo
        ? `https://player.twitch.tv/?video=${content}&parent=${hostname}`
        : `https://player.twitch.tv/?channel=${content}&parent=${hostname}`;

    return (
        <Box key={`twitch-${index}`} className="twitch-container" sx={{ margin: '16px 0' }}>
            <iframe
                src={src}
                allowFullScreen={true}
                height="378"
                width="100%"
                title={isVideo ? `Video de Twitch: ${content}` : `Canal de Twitch: ${content}`}
            ></iframe>
        </Box>
    );
};

// 🔽 Expresiones regulares para detección
// ✅ Mejorada para capturar el ID incluso si viene con parámetros de query (?s=20, etc.)
const tweetRegex = /https?:\/\/(?:www\.)?(?:x\.com|twitter\.com)\/\w+\/status\/(\d+)(?:\?[^\s<]*)?/g;
const twitchRegex = /(https?:\/\/(?:www\.)?twitch\.tv\/(?:videos\/(\d+)|([a-zA-Z0-9_]+)))/g;
const youtubeRegex = /(https?:\/\/(?:www\.)?(?:youtube\.com\/watch\?v=[\w-]+|youtu\.be\/[\w-]+))/g;

// 🔽 Procesador de URLs Twitch
const processTwitchUrl = (url) => {
    const videoMatch = url.match(/\/videos\/(\d+)/);
    if (videoMatch) {
        return <TwitchWidget content={videoMatch[1]} isVideo={true} index={`video-${videoMatch[1]}`} />;
    }
    const channelMatch = url.match(/twitch\.tv\/([a-zA-Z0-9_]+)/);
    if (channelMatch && !url.includes('/videos/')) {
        return (
            <TwitchWidget content={channelMatch[1]} isVideo={false} index={`channel-${channelMatch[1]}`} />
        );
    }
    return url;
};

// 🔽 Procesador de URLs YouTube
const processYoutubeUrl = (url, index) => {
    const component = <YoutubePreviewContent key={`youtube-${index}`} url={url} />;
    return component;
};

// 🔽 Procesamiento principal del contenido
export const getArticleContent = (content) => {
    if (!content) return null;

    let parts = [];
    let lastIndex = 0;
    let match;

    // 🐦 Procesar tweets
    while ((match = tweetRegex.exec(content)) !== null) {
        parts.push(content.slice(lastIndex, match.index));
        parts.push(<TweetWidget tweetId={match[1]} index={parts.length} />);
        lastIndex = match.index + match[0].length;
    }
    parts.push(content.slice(lastIndex));

    // 📺 Procesar Twitch y YouTube
    const processedParts = parts.map((part, partIndex) => {
        if (typeof part === 'string') {
            let mediaParts = [];
            lastIndex = 0;
            let mediaIndex = 0;

            const combinedRegex = new RegExp(`${twitchRegex.source}|${youtubeRegex.source}`, 'g');
            const twitchTestRegex = new RegExp(twitchRegex.source); // sin 'g'
            const youtubeTestRegex = new RegExp(youtubeRegex.source); // sin 'g'

            while ((match = combinedRegex.exec(part)) !== null) {
                const textBefore = part.slice(lastIndex, match.index);
                if (textBefore) {
                    mediaParts.push(parse(sanitizeHtml(textBefore, sanitizeOptions)));
                }

                const matchedUrl = match[0];
                if (twitchTestRegex.test(matchedUrl)) {
                    mediaParts.push(processTwitchUrl(matchedUrl));
                } else if (youtubeTestRegex.test(matchedUrl)) {
                    mediaParts.push(processYoutubeUrl(matchedUrl, `${partIndex}-${mediaIndex}`));
                    mediaIndex++;
                }

                lastIndex = match.index + matchedUrl.length;
            }

            const remainingText = part.slice(lastIndex);
            if (remainingText) {
                mediaParts.push(parse(sanitizeHtml(remainingText, sanitizeOptions)));
            }

            return mediaParts;
        }
        return part;
    });

    const finalResult = (
        <Box className="post-content article-post-content" sx={{ color: 'text.primary' }}>
            {processedParts.flat().map((part, index) => (
                <React.Fragment key={`content-${index}`}>{part}</React.Fragment>
            ))}
        </Box>
    );

    return finalResult;
};

const ArticleContent = ({ articulo, image, shareComponent = null }) => {
    const theme = useTheme();

    return (
        <Box className="noticia-page-component__noticia" color={theme.palette.text.primary}>
            <>
                <Box
                    className="article-content-image-container"
                    sx={{
                        mb: 1,
                        borderRadius: '8px',
                        overflow: 'hidden',
                        boxShadow: '0 2px 6px rgba(0,0,0,0.1)',
                    }}
                >
                    {articulo.video ? (
                        <LiteYoutubePreview url={articulo.video} height={440} autoplay={true} />
                    ) : (
                        <CustomImageNoticiasComponent
                            src={image}
                            alt={`Imagen de la noticia: ${articulo.title}`}
                            title={`Noticia: ${articulo.title}`}
                            role="img"
                            width={0}
                            height={0}
                            sizes="100vw"
                            style={{ width: '100%', height: 'auto', maxWidth: '100%' }}
                            priority={true}
                        />
                    )}
                </Box>

                <NoticiaFuente noticia={articulo} mb={1} />

                {shareComponent}

                {/* Contenido del artículo */}
                <Box>{getArticleContent(articulo.content)}</Box>
            </>
        </Box>
    );
};

export default ArticleContent;
